import { Link } from "react-router-dom";
import { formatDate, formatDateTime } from "../../api/dates";
import type { InvoiceRow } from "../../api/invoices";
import { formatMoney, formatRate, toCents } from "../../api/money";
import { jobLink } from "../commercial/pipeline";

/** A tax invoice to the City, as its job's Invoice step shows it. Read-only by
 * design -- what an invoice says is what went to the City; a wrong one is
 * credited and reissued, which is a commercial act, not a form field. The
 * register that was its own screen is the commercial rail now. */
export function InvoiceView({ invoice }: { invoice: InvoiceRow }) {
  const hasPenalty = (toCents(invoice.penalty) ?? 0) !== 0;
  const boq = invoice.boq;
  return (
    <section className="cm-docview" aria-label={`Invoice ${invoice.number}`}>
      <div className="cm-doc-head">
        <h2 className="mono">{invoice.number}</h2>
        <span className="cm-doc-meta">
          Issued {formatDateTime(invoice.issued_at)} by {invoice.issued_by ?? "—"} · not edited once issued
        </span>
        <a className="btn btn-primary cm-doc-end" href={invoice.pdf_url}>
          Download the tax invoice (PDF)
        </a>
      </div>
      <dl className="cm-facts">
        <div>
          <dt>Dated</dt>
          <dd>{formatDate(invoice.invoice_date)}</dd>
        </div>
        <div>
          <dt>Raised against</dt>
          <dd>
            {boq ? (
              <Link to={boq.quotation_id ? jobLink(boq.quotation_id, "boq") : `/boq/${boq.id}`}>{boq.name}</Link>
            ) : (
              "—"
            )}
          </dd>
        </div>
        <div>
          <dt>Site</dt>
          <dd>{invoice.site?.name ?? "not linked"}</dd>
        </div>
      </dl>
      <table className="cm-foot">
        <tbody>
          <tr>
            <td>{hasPenalty ? "Gross excluding VAT" : "Excluding VAT"}</td>
            <td>{formatMoney(invoice.gross_excl_vat)}</td>
          </tr>
          {hasPenalty ? (
            <>
              <tr>
                <td>Penalty at {formatRate(invoice.penalty_rate)}</td>
                <td>{formatMoney(invoice.penalty)}</td>
              </tr>
              <tr>
                <td>Excluding VAT</td>
                <td>{formatMoney(invoice.excl_vat)}</td>
              </tr>
            </>
          ) : null}
          <tr>
            <td>VAT at {formatRate(invoice.vat_rate)}</td>
            <td>{formatMoney(invoice.vat)}</td>
          </tr>
          <tr className="total">
            <td>Total</td>
            <td>{formatMoney(invoice.total_incl_vat)}</td>
          </tr>
        </tbody>
      </table>
      {invoice.boq_differs ? (
        <p className="notice warn">
          The BOQ now prices {formatMoney(invoice.boq_now_excl_vat)}, which is not what was invoiced. It has been edited
          since. The invoice is right about what went to the City; the difference is for somebody to resolve.
        </p>
      ) : null}
    </section>
  );
}
